import { Checkbox, Form, FormInput } from 'semantic-ui-react';

import type { GuardianState } from './GuardianSection';

/**
 * Who to call in an emergency — for **everyone**, not only minors.
 *
 * Name and number are required by the form (see `missingForRegistration`), not by the server: the
 * classic form shares the mutation, sends none of this, and has to keep saving.
 *
 * For a child whose guardian has been entered, "Same as parent/guardian" copies their details
 * across. Copied, not linked — the values are stored on their own, so correcting the guardian
 * later does not silently change who gets called, or the other way round.
 */

export interface EmergencyContactState {
    emergencyContactName: string;
    emergencyContactPhone: string;
    emergencyContactRelationship: string;
}

export const EMPTY_EMERGENCY_CONTACT: EmergencyContactState = {
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
};

/** Does the emergency contact currently say exactly what the guardian section says? */
const matchesGuardian = (value: EmergencyContactState, guardian: GuardianState): boolean =>
    guardian.guardianName.trim() !== ''
    && value.emergencyContactName === guardian.guardianName
    && value.emergencyContactPhone === guardian.guardianPhone
    && value.emergencyContactRelationship === guardian.guardianRelationship;

export const EmergencyContactSection = ({
    value,
    onChange,
    guardian,
    personIsMinor,
    showErrors,
}: {
    value: EmergencyContactState;
    onChange: (next: EmergencyContactState) => void;
    guardian: GuardianState;
    personIsMinor: boolean;
    showErrors: boolean;
}) => {
    const set = (patch: Partial<EmergencyContactState>) => onChange({ ...value, ...patch });

    // Offered only when there is a guardian to copy. Its checked state is *derived* from whether the
    // values match, rather than stored — so editing either side afterwards unticks it honestly
    // instead of leaving a tick that no longer describes the data.
    const canCopyGuardian = personIsMinor && guardian.guardianName.trim() !== '';
    const sameAsGuardian = canCopyGuardian && matchesGuardian(value, guardian);

    return (
        <div className="creator-section">
            <div className="creator-section-title">Emergency contact</div>
            <p className="creator-section-hint">Who to call if something happens. Needed for everyone.</p>

            {canCopyGuardian && (
                <Form.Field>
                    <Checkbox
                        id="emergency-same-as-guardian"
                        label="Same as parent/guardian"
                        checked={sameAsGuardian}
                        onChange={(_, d) => onChange(d.checked
                            ? {
                                emergencyContactName: guardian.guardianName,
                                emergencyContactPhone: guardian.guardianPhone,
                                emergencyContactRelationship: guardian.guardianRelationship,
                            }
                            : EMPTY_EMERGENCY_CONTACT)}
                    />
                </Form.Field>
            )}

            <Form.Group widths="equal">
                <FormInput
                    fluid
                    label="Emergency contact name"
                    placeholder="Full name"
                    error={showErrors && value.emergencyContactName.trim() === '' && 'Please enter a name'}
                    value={value.emergencyContactName}
                    onChange={(e) => set({ emergencyContactName: e.target.value })}
                />
                <FormInput
                    fluid
                    label="Emergency contact number"
                    placeholder="Phone number"
                    error={showErrors && value.emergencyContactPhone.trim() === '' && 'Please enter a number'}
                    value={value.emergencyContactPhone}
                    onChange={(e) => set({ emergencyContactPhone: e.target.value })}
                />
            </Form.Group>

            <FormInput
                fluid
                label="Relationship"
                placeholder="Mother, husband, friend, neighbour…"
                value={value.emergencyContactRelationship}
                onChange={(e) => set({ emergencyContactRelationship: e.target.value })}
            />
        </div>
    );
};
